/**
 * JapanETCcard team — the same 5-member AI Operations team as NoJo Enterprise,
 * but wearing the JapanETCcard branded shirts. Used on the JapanETCcard
 * service detail page (`/:lang/services/japanetccard`) to show the team that
 * directly handles ETC customer requests.
 *
 * Portrait images live in /public/team/japanetccard/.
 *
 * Slugs and role meta match `src/data/aiTeam.js` so the same identities
 * carry across the corporate site and each service-detail page.
 */

export const JEC_TEAM = [
  {
    slug: "sora",
    portrait: "/team/japanetccard/sora.webp",
    nameEn: "Sora",
    nameJa: "ソラ",
    titleEn: "ETC Operations Director",
    titleJa: "ETCオペレーションズ・ディレクター",
    role: "director",
    bioEn:
      "Sora signs off on every ETC card application, billing cycle, and customer-impacting change before it goes out. Final approval lives with Sora.",
    bioJa:
      "ETCカードの申請、請求サイクル、お客様に影響する変更について、ソラは確認ポイントの整理を支援します。最終承認が必要な内容は、人によるレビューを経て対応します。",
  },
  {
    slug: "rin",
    portrait: "/team/japanetccard/rin.webp",
    nameEn: "Rin",
    nameJa: "リン",
    titleEn: "ETC QA Lead",
    titleJa: "ETC QA リード",
    role: "qa",
    bioEn:
      "Rin cross-checks every ETC application packet, every invoice, and every account change. Edge cases route to a human, never auto-resolved.",
    bioJa:
      "ETCカードの申請パケット、請求書、アカウント変更について、リンは相互チェックを支援します。判断が必要なケースは必ず人によるレビューに回します。",
  },
  {
    slug: "yuki",
    portrait: "/team/japanetccard/yuki.webp",
    nameEn: "Yuki",
    nameJa: "ユキ",
    titleEn: "Senior AI Analyst — Applications",
    titleJa: "シニアAIアナリスト・申請担当",
    role: "analyst",
    bioEn:
      "Yuki processes incoming ETC card applications — verifying residency status, ID documents, and shipping requirements in both Japanese and English.",
    bioJa:
      "ETCカードの新規申請を担当。在留資格、本人確認書類、発送要件を日英の両方で確認します。",
  },
  {
    slug: "hana",
    portrait: "/team/japanetccard/hana.webp",
    nameEn: "Hana",
    nameJa: "ハナ",
    titleEn: "Senior AI Analyst — Billing",
    titleJa: "シニアAIアナリスト・請求担当",
    role: "analyst",
    bioEn:
      "Hana handles monthly invoices, year-end ETC tax reports, and reconciliation of toll discounts and commuter mileage credits.",
    bioJa:
      "月次請求書、年間ETC利用レポート、各種ETC割引・マイレージの突合せを担当します。",
  },
  {
    slug: "aoi",
    portrait: "/team/japanetccard/aoi.webp",
    nameEn: "Aoi",
    nameJa: "アオイ",
    titleEn: "Senior AI Analyst — Support",
    titleJa: "シニアAIアナリスト・サポート担当",
    role: "analyst",
    bioEn:
      "Aoi answers ETC customer questions in English and Japanese — billing dashboard help, deployment-month pauses, replacement cards, anything the customer needs.",
    bioJa:
      "ETCご利用者様からの日英のお問い合わせを担当。請求ダッシュボード、派遣中の請求停止、カード再発行など、お客様が必要なすべての場面に対応します。",
  },
];

/**
 * Group photo gallery for the JapanETCcard detail page hero/gallery.
 * Each photo carries a bilingual location/caption so the gallery story
 * stays coherent (HQ in Yokosuka → out at toll gates → in the office
 * processing applications → on the road).
 */
export const JEC_TEAM_GROUP_PHOTOS = [
  {
    id: "yokosuka-gate",
    src: "/team/japanetccard/group-yokosuka-gate.webp",
    locationEn: "Yokosuka Toll Gate · HQ city",
    locationJa: "横須賀料金所・本社所在地",
    captionEn: "At the Yokosuka toll gate — the team in our HQ city.",
    captionJa: "横須賀料金所にて ─ 本社所在地のチーム。",
  },
  {
    id: "tokyo-gate",
    src: "/team/japanetccard/group-tokyo-gate.webp",
    locationEn: "Tokyo Toll Gate",
    locationJa: "東京料金所",
    captionEn: "At the Tokyo toll gate — where ETC traffic flows daily.",
    captionJa: "東京料金所にて ─ ETCが毎日流れる現場。",
  },
  {
    id: "tokyo-blossom",
    src: "/team/japanetccard/group-tokyo-blossom.webp",
    locationEn: "Tokyo · Spring",
    locationJa: "東京・春",
    captionEn: "Tokyo Tower in the background. Spring sakura. Same team.",
    captionJa: "東京タワーを背景に、春の桜。変わらないチーム。",
  },
  {
    id: "workflow",
    src: "/team/japanetccard/group-workflow-1.webp",
    locationEn: "Office · Document Processing",
    locationJa: "オフィス・書類処理",
    captionEn: "Processing ETC applications and invoices in the office.",
    captionJa: "ETCの申請や請求書の処理を行うオフィスの様子。",
  },
  {
    id: "ocr",
    src: "/team/japanetccard/group-ocr.webp",
    locationEn: "Office · AI Analysis",
    locationJa: "オフィス・AI 分析",
    captionEn: "AI-assisted document review for accuracy checks.",
    captionJa: "正確性を確認するためのAIによる書類レビュー。",
  },
  {
    id: "workflow-2",
    src: "/team/japanetccard/group-workflow-2.webp",
    locationEn: "Office · Workflow Review",
    locationJa: "オフィス・ワークフロー確認",
    captionEn: "Reviewing the daily processing workflow as a team.",
    captionJa: "毎日の処理ワークフローをチームで確認しています。",
  },
];

export const JEC_TEAM_MANIFESTO = {
  eyebrowEn: "The five members in charge of every ETC card",
  eyebrowJa: "ETCカード一枚一枚を支える5つのAI支援ロール",
  titleEn: "The five members in charge of every ETC card we issue.",
  titleJa: "発行するETCカード一枚一枚を支える、5つのAI支援ロール。",
  leadEn:
    "Every ETC card application, every monthly invoice, every customer question — handled by the same five-member system, with a human final approval on anything that affects a customer.",
  leadJa:
    "ETCカードの申請、月次請求、お客様からのお問い合わせ ─ そのすべてを、同じAI支援ワークフローが支えています。お客様に影響する判断はすべて、人による最終承認を経由します。",
};
